import isNaN from 'lodash/isNaN'


declare const gt: any
declare const $: any

export const Vector = class Vector {
  x: any
  y: any

  constructor(x: any, y: any) {
    this.x = x
    this.y = y
  }

  static random(minx = 0, maxx: any = null, miny = 0, maxy: any = null) {
    if (maxx == null) {
      maxx = gt.mainDiv.width()
    }
    if (maxy == null) {
      maxy = gt.mainDiv.height()
    }
    return new Vector(Math.random() * (maxx - minx) + minx, Math.random() * (maxy - miny) + miny)
  }

  rotate(rads: any) {
    return new Vector(this.x * Math.cos(rads) - this.y * Math.sin(rads), this.x * Math.sin(rads) + this.y * Math.cos(rads))
  }

  magnitude() {
    const mag = Math.sqrt(this.x * this.x + this.y * this.y)
    if (isNaN(mag)) {
      return 0
    }
    return mag
  }

  add(o: any) {
    return new Vector(this.x + o.x, this.y + o.y)
  }

  sub(o: any) {
    return new Vector(this.x - o.x, this.y - o.y)
  }

  mul(s: any) {
    return new Vector(this.x * s, this.y * s)
  }

  div(s: any) {
    if (s == 0) {
      throw TypeError('cannot divide by zero')
    }
    return new Vector(this.x / s, this.y / s)
  }

  neg() {
    return this.mul(-1.0)
  }

  normalize() {
    if (this.magnitude() == 0) {
      return new Vector(0, 0)
    }
    return this.div(this.magnitude())
  }

  distance(o: any) {
    return this.sub(o).magnitude()
  }

  dot(o: any) {
    return this.x * o.x + this.y * o.y
  }

  angle(o: any) {
    let deltax = o.x - this.x
    if (deltax == 0) {
      deltax = 0.0001 //sloppy
    }
    const deltay = o.y - this.y
    let out = Math.atan(deltay / deltax) % (2 * Math.PI)
    if (o.x >= this.x) {
      out += Math.PI
    }
    return out
  }

  toString() {
    return `(${this.x}, ${this.y})`
  }

  closer(p1: any, p2: any) {
    //find the closer of p1 and p2 to @.  if equidistant, return p1
    const d1 = this.distance(p1)
    const d2 = this.distance(p2)
    if (d1 > d2) {
      //if p2 is closer
      return p2
    }
    return p1
  }

  rotate_m(rads: any) {
    const x = this.x
    this.x = x * Math.cos(rads) - this.y * Math.sin(rads)
    this.y = x * Math.sin(rads) + this.y * Math.cos(rads)
    return this
  }

  add_m(x: any, y: any) {
    this.x += x
    this.y += y
    return this
  }

  sub_m(x: any, y: any) {
    this.x -= x
    this.y -= y
    return this
  }

  mul_m(s: any) {
    this.x *= s
    this.y *= s
    return this
  }

  div_m(s: any) {
    if (s == 0) {
      throw TypeError('cannot divide by zero')
    }
    this.x /= s
    this.y /= s
    return this
  }

  neg_m() {
    this.x *= -1
    this.y *= -1
    return this
  }

  normalize_m() {
    const m = this.magnitude()
    if (m != 0) {
      this.div_m(m)
    }
    return this
  }

  copy() {
    return new Vector(this.x, this.y)
  }

  set_m(x: any, y: any) {
    this.x = x
    this.y = y
  }
}

export const Line = class Line {
  start: any
  end: any

  constructor(start: any, end: any) {
    this.start = start
    this.end = end
  }

  midpoint() {
    return this.start.copy().add_m(this.end.x / 2.0, this.end.y / 2.0)
  }

  length() {
    const a = this.end.x - this.start.x
    const b = this.end.y - this.start.y
    return Math.sqrt(a * a + b * b)
  }

  intersect(o: any) {
    const denom = (o.end.y - o.start.y) * (this.end.x - this.start.x) - (o.end.x - o.start.x) * (this.end.y - this.start.y)

    //if the lines are parallel
    if (denom == 0) {
      return false
    }

    const ua = ((o.end.x - o.start.x) * (this.start.y - o.start.y) - (o.end.y - o.start.y) * (this.start.x - o.start.x)) / denom
    const ub = ((this.end.x - this.start.x) * (this.start.y - o.start.y) - (this.end.y - this.start.y) * (this.start.x - o.start.x)) / denom

    if (ua < 0 || ua > 1 || ub < 0 || ub > 1) {
      return false
    }

    return new Vector(this.start.x + ua * (this.end.x - this.start.x), this.start.y + ua * (this.end.y - this.start.y))
  }

  toString() {
    return `[Line from ${this.start.toString()} to ${this.end.toString()}]`
  }

  originizedVector() {
    return this.end.sub(this.start) //move the line's start point to the origin, then return that as a vector
  }

  rotateAroundStart(rads: any) {
    let o = this.originizedVector()
    o = o.rotate(rads)
    o = o.add(this.start)
    return new Line(this.start, o)
  }

  orthogonals(): [Line, Line] {
    const l = new Line(this.midpoint(), this.end)
    return [l.rotateAroundStart(Math.PI / 2.0), l.rotateAroundStart(-Math.PI / 2.0)]
  }

  orthogonalByPoint(p: any) {
    const [o1, o2] = this.orthogonals()
    const d1 = o1.end.distance(p)
    const d2 = o2.end.distance(p)
    if (d1 < d2) {
      return o1
    }
    return o2
  }

  closestPointOnSegment(p: any) {
    //adapted from http://stackoverflow.com/questions/849211/shortest-distance-between-a-point-and-a-line-segment
    const b = this.originizedVector()
    const key_dot = b.dot(p.sub(this.start)) //key dot product
    const b_len_sq = b.dot(b) //Segment length squared

    if (key_dot <= 0) {
      //pt is "behind" st, use st
      return this.start
    }
    if (key_dot >= b_len_sq) {
      //pt is "past" end of segment, use end (notice we are saving twin sqrts here cuz)
      return this.start.add(b)
    }
    //pt is inside segment, reuse key_dot and bLenSq to get percent of segment to move in to find closest point
    const key_dot_to_pct_of_b = key_dot / b_len_sq //dot product comes squared
    return new Vector(b.x * key_dot_to_pct_of_b + this.start.x, b.y * key_dot_to_pct_of_b + this.start.y)
  }

  distanceToPoint(p: any) {
    const nearest_p = this.closestPointOnSegment(p)
    return p.sub(nearest_p).magnitude()
  }

  draw(color = 'red') {
    gt.drawLine(this.start, this.end, color)
  }
}

export const Ellipse = class Ellipse {
  a: any
  b: any

  constructor(a: any, b: any) {
    this.a = a
    this.b = b
  }

  scale(scalar: any) {
    return new Ellipse(this.a * scalar, this.b * scalar)
  }

  getRadius(angle: any) {
    const b_part = this.b * Math.cos(angle)
    const a_part = this.a * Math.sin(angle)
    const denom = Math.sqrt(b_part * b_part + a_part * a_part)
    return (this.a * this.b) / denom
  }

  getPoint(angle: any, center: any) {
    return new Vector(this.getRadius(angle), 0).rotate_m(angle).add_m(center.x, center.y)
  }
}

export const Box = class Box {
  tl: any
  width: any
  height: any

  constructor(tl: any, width: any, height: any) {
    this.tl = tl
    this.width = width
    this.height = height
  }

  calc_sides_() {
    //points representing the corners
    ;(this as any).tr = new Vector(this.tl.x + this.width, this.tl.y)
    ;(this as any).bl = new Vector(this.tl.x, this.tl.y + this.height)
    ;(this as any).br = new Vector((this as any).bl.x + this.width, (this as any).bl.y)

    //lines representing the sides
    ;(this as any).t = new Line(this.tl, (this as any).tr)
    ;(this as any).b = new Line((this as any).bl, (this as any).br)
    ;(this as any).l = new Line(this.tl, (this as any).bl)
    ;(this as any).r = new Line((this as any).tr, (this as any).br)
    ;(this as any).calc_sides_ = () => {}
  }

  sides() {
    this.calc_sides_()
    ;(this as any).sides = () => [(this as any).t, (this as any).b, (this as any).l, (this as any).r]
    return (this as any).sides()
  }

  static fromDiv(div: any) {
    div = $(div)
    const { left: x, top: y } = div.position()
    const w = div.outerWidth()
    const h = div.outerHeight()
    return new Box(new Vector(x, y), w, h)
  }

  intersectLine(l: any) {
    this.calc_sides_()
    ;(this as any).intersectLine = (line: any) => {
      //note: if the line intersects the box twice, then this only returns one of them
      let result: any = null
      if ((result = (this as any).t.intersect(line))) return result
      if ((result = (this as any).b.intersect(line))) return result
      if ((result = (this as any).l.intersect(line))) return result
      if ((result = (this as any).r.intersect(line))) return result
      return false
    }
    return (this as any).intersectLine(l)
  }

  centerTo(p: any) {
    return new Box(new Vector(p.x - this.width / 2.0, p.y - this.height / 2.0), this.width, this.height)
  }

  isPointInside(p: any) {
    if (p.x > this.tl.x || p.y > this.tl.y) {
      if (!(this as any).br) {
        ;(this as any).br = new Vector(this.tl.x + this.width, this.tl.y + this.height)
      }
      if (p.x < (this as any).br.x && p.y < (this as any).br.y) {
        return true
      }
    }
    return false
  }
}
